import type { ParsedUrlQuery } from 'querystring'
import { useState, useMemo } from 'react'
import { useRouter } from 'next/router'
// @mui
import { Box, Container, Stack, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
// hooks
import useSettings from '@/hooks/useSettings'
import useLocales from '@/hooks/useLocales'
import useApp from '@/hooks/useApp'
import useResponsive from '@/hooks/useResponsive'
import useCollapseDrawer from '@/hooks/useCollapseDrawer'
import { MerchandisePreview } from '@/types/merchandise'
// layouts
import Layout from '@/layouts/index'
// components
import Page from '@/components/Page'
import { SkeletonProductItem } from '@/components/skeleton'
// sections
import {
  ShopProductCard,
  ShopProductSearch,
  ShopProductSort,
  ShopFilterSidebar,
  ShopFilterTopBar,
  ShopTagFiltered,
} from '@/sections/e-commerce/shop'
import { productService } from '@/services/product'
import { settingService, GlobalSetting } from '@/services/page'
import pathService from '@/services/path'
import { ProductsOrder, TagsForAllProductsFragment } from '@/generated/graphql'
import type { GetStaticPaths, GetStaticProps } from 'next'
import { APPS } from '@/appConfig'
import find from 'lodash/find'
import { ShopProductSortForPage } from '@/sections/e-commerce/shop/ShopProductSort'
import { HEADER, NAVBAR } from '@/config'

interface PathProps extends ParsedUrlQuery {
  appId: string
}

interface ProductsPageProps {
  preview: boolean
  totalProducts: number
  globalSetting?: GlobalSetting
  allProducts: MerchandisePreview[]
  allSearchTags: TagsForAllProductsFragment[]
}

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  paddingTop: theme.spacing(8),
  [theme.breakpoints.up('md')]: {
    paddingTop: theme.spacing(11),
  },
}))

type MainStyleProps = {
  collapseClick: boolean
}

const MainStyle = styled('main', {
  shouldForwardProp: (prop) => prop !== 'collapseClick',
})<MainStyleProps>(({ collapseClick, theme }) => ({
  flexGrow: 1,
  paddingTop: 24,
  paddingBottom: HEADER.MOBILE_HEIGHT + 24,
  [theme.breakpoints.up('lg')]: {
    paddingLeft: 16,
    paddingRight: 16,
    paddingBottom: HEADER.DASHBOARD_DESKTOP_HEIGHT + 24,
    width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH}px)`,
    transition: theme.transitions.create('margin-left', {
      duration: theme.transitions.duration.shorter,
    }),
    ...(collapseClick && {
      marginLeft: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
    }),
  },
}))

// ----------------------------------------------------------------------

Products.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout variant="main">{page}</Layout>
}

// ----------------------------------------------------------------------
const DEFAULT_PRODUCTS_ORDERS = ProductsOrder.SysPublishedAtDesc

export default function Products({
  allProducts = [],
  allSearchTags = [],
  totalProducts = 0,
}: ProductsPageProps) {
  const { themeStretch } = useSettings()
  const { collapseClick, isCollapse } = useCollapseDrawer()
  const isDesktop = useResponsive('up', 'lg')
  const { locale, query } = useRouter()
  const { logo, host } = useApp()
  const [openFilter, setOpenFilter] = useState(false)

  const [filter, setFilter] = useState<{
    search: string | null
    tagIds: string[] | null
    sortBy: ProductsOrder | null
  }>({
    search: null,
    tagIds: null,
    sortBy: null,
  })
  const { translate } = useLocales()

  const querySearch = useMemo<{
    search: string
    tagIds: string[]
    sortBy: ProductsOrder
  }>(
    () => ({
      search: Array.isArray(query.search) ? '' : query.search ?? '',
      tagIds: !Array.isArray(query.tags)
        ? query.tags
            ?.split('|')
            .map((tag) => tag.split('_')[0])
            .filter((tagIdParam) => find(allSearchTags, { sys: { id: tagIdParam } })) ?? []
        : [],
      sortBy: Array.isArray(query.sort)
        ? DEFAULT_PRODUCTS_ORDERS
        : query.sort
        ? Object.values(ProductsOrder).includes(query.sort as ProductsOrder)
          ? (query.sort as ProductsOrder)
          : DEFAULT_PRODUCTS_ORDERS
        : DEFAULT_PRODUCTS_ORDERS,
    }),
    [query, allSearchTags]
  )

  const combinedFilter = useMemo<{
    search: string
    tagIds: string[]
    sortBy: ProductsOrder
  }>(
    () => ({
      search: filter.search ?? querySearch.search,
      tagIds: filter.tagIds ?? querySearch.tagIds,
      sortBy: filter.sortBy ?? querySearch.sortBy,
    }),
    [filter, querySearch]
  )

  const filteredProducts = useMemo(
    () =>
      allProducts
        .sort((a, b) => {
          switch (combinedFilter.sortBy) {
            case ProductsOrder.SysPublishedAtAsc:
              return new Date(a.publishedAt).getTime() - new Date(b.publishedAt).getTime()
            case ProductsOrder.PriceAsc:
              return (a.price ?? 0) - (b.price ?? 0)
            case ProductsOrder.PriceDesc:
              return (b.price ?? 0) - (a.price ?? 0)
            case ProductsOrder.TitleAsc:
              if (a.title.toLowerCase() > b.title.toLowerCase()) return 1
              if (a.title.toLowerCase() < b.title.toLowerCase()) return -1
              else return 0
            case ProductsOrder.TitleDesc:
              if (b.title.toLowerCase() > a.title.toLowerCase()) return 1
              if (b.title.toLowerCase() < a.title.toLowerCase()) return -1
              else return 0
            default:
              return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
          }
        })
        .filter((product) => {
          if (combinedFilter.tagIds.length && combinedFilter.search) {
            return (
              product.tags.some((tag) => combinedFilter.tagIds.includes(tag.id)) &&
              (product.title.toLowerCase().includes(combinedFilter.search.toLowerCase()) ||
                product.abstract.toLowerCase().includes(combinedFilter.search.toLowerCase()) ||
                product.tags.find((tag) =>
                  tag.name.toLowerCase().includes(combinedFilter.search.toLowerCase())
                ))
            )
          }
          if (combinedFilter.search) {
            return (
              product.title.toLowerCase().includes(combinedFilter.search.toLowerCase()) ||
              product.abstract.toLowerCase().includes(combinedFilter.search.toLowerCase()) ||
              product.tags.find((tag) =>
                tag.name.toLowerCase().includes(combinedFilter.search.toLowerCase())
              )
            )
          }
          if (combinedFilter.tagIds.length) {
            return product.tags.some((tag) => combinedFilter.tagIds.includes(tag.id))
          }

          return true
        }),
    [allProducts, combinedFilter]
  )

  const handleOpenFilter = () => {
    setOpenFilter(true)
  }

  const handleCloseFilter = () => {
    setOpenFilter(false)
  }

  const handleResetFilter = () => {
    setFilter((prev) => ({ ...prev, tagIds: [] }))
    handleCloseFilter()
  }
  const renderContent = (
    <>
      <Stack
        spacing={2}
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 5, mt: 3 }}
      >
        <ShopProductSearch
          value={combinedFilter.search}
          onSearch={(value) =>
            setFilter((prev) => ({
              ...prev,
              search: value,
            }))
          }
        />

        <Stack direction="row" spacing={1} sx={{ my: 1 }}>
          <ShopProductSort
            forPage={ShopProductSortForPage.product}
            value={combinedFilter.sortBy}
            onSort={(value) =>
              setFilter((prev) => ({
                ...prev,
                sortBy: value,
              }))
            }
          />
        </Stack>
      </Stack>
      <Stack sx={{ mb: 3 }}>
        {Boolean(combinedFilter.tagIds?.length) && (
          <>
            <Typography variant="body2" gutterBottom>
              <strong>{filteredProducts.length}</strong>
              &nbsp;Products found
            </Typography>

            <ShopTagFiltered
              allSearchTags={allSearchTags}
              filters={{
                tagIds: combinedFilter.tagIds,
              }}
              onRemoveTagId={(id) =>
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  tagIds: prevFilter.tagIds
                    ? prevFilter.tagIds.filter((tagId) => tagId !== id)
                    : combinedFilter.tagIds.filter((tagId) => tagId !== id),
                }))
              }
              onResetAll={handleResetFilter}
            />
          </>
        )}
      </Stack>
      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: {
            xs: 'repeat(1, 1fr)',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)',
          },
        }}
      >
        {filteredProducts.map((product, index) =>
          product ? (
            <ShopProductCard key={product.id} product={product} />
          ) : (
            <SkeletonProductItem key={index} />
          )
        )}
      </Box>
    </>
  )

  return (
    <Page
      title={translate('common.product')}
      jsonLd={{
        '@context': 'https://schema.org',
        '@type': 'ProductCollection',
        name: translate('common.product'),
        description: 'A collection products.',
        url: `https://${host}/${locale}/products`,
        image: logo ?? '',
        includesObject: filteredProducts.map((filteredProduct) => ({
          '@type': 'Product',
          '@id': filteredProduct.id,
          name: filteredProduct.title,
          image: filteredProduct.previewUrl ?? '',
          description: filteredProduct.abstract,
          url: `https://${host}/${locale}/product/${filteredProduct.slug}`,
        })),
      }}
    >
      <RootStyle>
        {isDesktop ? (
          <Box
            sx={{
              display: { lg: 'flex' },
              minHeight: { lg: 1 },
            }}
          >
            <ShopFilterSidebar
              filteredTagIds={combinedFilter.tagIds}
              filterTags={allSearchTags}
              onAddTagId={(id) =>
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  tagIds: prevFilter.tagIds
                    ? [...prevFilter.tagIds, id]
                    : [...combinedFilter.tagIds, id],
                }))
              }
              onRemoveTagId={(id) =>
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  tagIds: prevFilter.tagIds
                    ? prevFilter.tagIds.filter((tagId) => tagId !== id)
                    : combinedFilter.tagIds.filter((tagId) => tagId !== id),
                }))
              }
              onResetAll={handleResetFilter}
              isOpen={openFilter}
              onOpen={handleOpenFilter}
              onClose={handleCloseFilter}
            />
            <MainStyle collapseClick={collapseClick}>{renderContent}</MainStyle>
          </Box>
        ) : (
          <>
            <ShopFilterTopBar
              filteredTagIds={combinedFilter.tagIds}
              filterTags={allSearchTags}
              onAddTagId={(id) =>
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  tagIds: prevFilter.tagIds
                    ? [...prevFilter.tagIds, id]
                    : [...combinedFilter.tagIds, id],
                }))
              }
              onRemoveTagId={(id) =>
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  tagIds: prevFilter.tagIds
                    ? prevFilter.tagIds.filter((tagId) => tagId !== id)
                    : combinedFilter.tagIds.filter((tagId) => tagId !== id),
                }))
              }
              onResetAll={handleResetFilter}
              isOpen={openFilter}
              onOpen={handleOpenFilter}
              onClose={handleCloseFilter}
            />
            <Container maxWidth={themeStretch ? false : 'lg'}>
              <Box
                component="main"
                sx={{
                  px: { lg: 2 },
                  pt: {
                    xs: `${HEADER.MOBILE_HEIGHT + 24}px`,
                    lg: `${HEADER.DASHBOARD_DESKTOP_HEIGHT + 80}px`,
                  },
                  pb: {
                    xs: `${HEADER.MOBILE_HEIGHT + 24}px`,
                    lg: `${HEADER.DASHBOARD_DESKTOP_HEIGHT + 24}px`,
                  },
                }}
              >
                {renderContent}
              </Box>
            </Container>
          </>
        )}
      </RootStyle>
    </Page>
  )
}

export const getStaticPaths: GetStaticPaths<PathProps> = async () => {
  const { getProductsPaths } = pathService()
  const paths = APPS.flatMap((app) => getProductsPaths(app.id))

  return {
    paths,
    fallback: true,
  }
}

export const getStaticProps: GetStaticProps<ProductsPageProps, PathProps> = async ({
  preview = false,
  params,
  locale,
}) => {
  if (!params) throw new Error('No path parameters found')
  const { appId } = params
  const { getAllProducts, getAllProductSearchTags, getAllProductsTotal } = productService(appId)
  const { getGlobalSetting } = settingService(appId)
  const globalSetting = await getGlobalSetting({ locale })

  const allProducts =
    (await getAllProducts({ locale, getAll: true, orders: [DEFAULT_PRODUCTS_ORDERS] })) ?? []

  const allSearchTags = await getAllProductSearchTags({ locale })
  const totalProducts = await getAllProductsTotal({ locale })

  return {
    props: {
      preview,
      totalProducts,
      globalSetting,
      allProducts,
      allSearchTags,
    },
    revalidate: 3600,
  }
}
